/* Browser WebAuthn helpers for passkeys (Touch ID, Face ID, Windows Hello, security keys).
 *
 * The API serializes binary fields (challenge, credential ids, user handle) as base64url
 * strings; the browser's credentials API wants ArrayBuffers. These helpers translate in
 * both directions and keep the components free of encoding noise.
 */

export function isPasskeySupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

function toBuffer(base64url) {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function toBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Runs the browser's "create passkey" ceremony from server-issued creation options. */
export async function createPasskey(options) {
  const publicKey = {
    ...options,
    challenge: toBuffer(options.challenge),
    user: { ...options.user, id: toBuffer(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: toBuffer(c.id),
    })),
  };
  const credential = await navigator.credentials.create({ publicKey });
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    extensions: credential.getClientExtensionResults(),
    response: {
      attestationObject: toBase64Url(credential.response.attestationObject),
      clientDataJSON: toBase64Url(credential.response.clientDataJSON),
      transports: credential.response.getTransports ? credential.response.getTransports() : [],
    },
  };
}

/** Runs the browser's "sign in with passkey" ceremony from server-issued assertion options. */
export async function getPasskeyAssertion(options) {
  const publicKey = {
    ...options,
    challenge: toBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: toBuffer(c.id),
    })),
  };
  const credential = await navigator.credentials.get({ publicKey });
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    extensions: credential.getClientExtensionResults(),
    response: {
      authenticatorData: toBase64Url(credential.response.authenticatorData),
      clientDataJSON: toBase64Url(credential.response.clientDataJSON),
      signature: toBase64Url(credential.response.signature),
      userHandle: credential.response.userHandle ? toBase64Url(credential.response.userHandle) : null,
    },
  };
}

/** A friendly default label for a new passkey based on the current platform. */
export function suggestPasskeyName() {
  const ua = navigator.userAgent;
  if (/Macintosh/.test(ua)) return 'Mac (Touch ID)';
  if (/iPhone|iPad/.test(ua)) return 'iPhone / iPad (Face ID)';
  if (/Windows/.test(ua)) return 'Windows Hello';
  if (/Android/.test(ua)) return 'Android device';
  return 'Passkey';
}
